import React, { useEffect, useState } from "react";
import {
	getGptModel,
	getTranslateGptModel,
	getTranslateProvider,
	setGptModel as saveGptModel,
	setTranslateGptModel as saveTranslateGptModel,
	setTranslateProvider,
	testGpt,
	testTranslate,
} from "../../api/apiServiceFetch";
import MediaContentTypeCard from "./MediaContentTypeCard";
import MediaSettingsCard from "./MediaSettingsCard";
import WebPushKeysCard from "./WebPushKeysCard";
import "./Settings.scss";

// Values must match the backend whitelist (Config.TranslateProvider).
const PROVIDERS = [
	{ value: "google", label: "Google Translate" },
	{ value: "yandex", label: "Yandex Translate" },
	{ value: "chatgpt", label: "ChatGPT" },
];

// Языки для проверки переводчика. Набор — те языки, на которых работает
// приложение; добавить новый можно одной строкой, значение — код языка, его
// сервер и передаёт сервису переводов.
const TRANSLATE_LANGS = [
	{ value: "ru", label: "Русский" },
	{ value: "en", label: "Английский" },
	{ value: "hy", label: "Армянский" },
	{ value: "fr", label: "Французский" },
];

// Совпадает с пределом на сервере (ModerationController.TranslateTestMaxLength).
const TRANSLATE_TEST_MAX_LENGTH = 2000;

// Список моделей GPT. Бэкенд белого списка не держит намеренно — новую модель
// достаточно дописать сюда одной строкой, а до этого её можно ввести вручную
// через вариант «Другая».
// Идентификаторы сверены с документацией OpenAI 11.09.2026. Порядок — от самой
// способной к самой дешёвой, прежнее поколение снизу.
//
// ВАЖНО: строка модели уходит и в наш прокси gpt.jerm.am, и напрямую в OpenAI.
// Поддерживает ли прокси новые модели, отсюда не проверить, поэтому после смены
// стоит убедиться, что разбор совместимости в приложении продолжает отвечать.
const GPT_MODELS = [
	{ value: "gpt-6-astra", label: "gpt-6-astra — самая способная" },
	{ value: "gpt-5.6", label: "gpt-5.6 — флагман (алиас gpt-5.6-sol)" },
	{ value: "gpt-5.6-terra", label: "gpt-5.6-terra — баланс цены и качества" },
	{ value: "gpt-5.6-luna", label: "gpt-5.6-luna — самая дешёвая из 5.6" },
	{ value: "gpt-4o", label: "gpt-4o — используется сейчас" },
	{ value: "gpt-4o-mini", label: "gpt-4o-mini — дешёвая из прошлого поколения" },
];

// Совпадает с запасным значением на сервере: пока модель не выбрана, работает оно.
const DEFAULT_GPT_MODEL = "gpt-4o";

const CUSTOM_GPT_MODEL = "__custom__";

// Пустая модель переводчика — это не «ничего не выбрано», а осмысленный вариант:
// сервис переводов работает моделью из собственных настроек, и сервер тогда не
// передаёт ему модель вовсе.
const SERVICE_DEFAULT_GPT_MODEL = "";

// Совпадает с пределом на сервере (GPTController.TestPromptMaxLength).
const GPT_TEST_MAX_LENGTH = 2000;

const GPT_SOURCE_LABELS = {
	jerm: "наш прокси gpt.jerm.am",
	openai: "OpenAI напрямую",
};

const Settings = () => {
	const [provider, setProvider] = useState("");
	const [savedProvider, setSavedProvider] = useState("");
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [status, setStatus] = useState(null); // { ok: bool, text: string }

	const [gptModel, setGptModelValue] = useState(DEFAULT_GPT_MODEL);
	const [savedGptModel, setSavedGptModel] = useState(DEFAULT_GPT_MODEL);
	const [isGptCustom, setIsGptCustom] = useState(false);
	const [isGptLoading, setIsGptLoading] = useState(true);
	const [isGptSaving, setIsGptSaving] = useState(false);
	const [gptStatus, setGptStatus] = useState(null);

	const [translateGptModel, setTranslateGptModelValue] = useState(
		SERVICE_DEFAULT_GPT_MODEL,
	);
	const [savedTranslateGptModel, setSavedTranslateGptModel] = useState(
		SERVICE_DEFAULT_GPT_MODEL,
	);
	const [isTranslateGptCustom, setIsTranslateGptCustom] = useState(false);
	const [isTranslateGptLoading, setIsTranslateGptLoading] = useState(true);
	const [isTranslateGptSaving, setIsTranslateGptSaving] = useState(false);
	const [translateGptStatus, setTranslateGptStatus] = useState(null);

	const [translateText, setTranslateText] = useState("");
	const [translateLang, setTranslateLang] = useState(TRANSLATE_LANGS[0].value);
	const [isTranslating, setIsTranslating] = useState(false);
	const [translateResult, setTranslateResult] = useState(null); // ответ /moderation/translateTest
	const [translateError, setTranslateError] = useState(null); // сетевая ошибка/400

	const [testPrompt, setTestPrompt] = useState("");
	const [isTesting, setIsTesting] = useState(false);
	const [testResult, setTestResult] = useState(null); // ответ /gpt/test
	const [testError, setTestError] = useState(null); // сетевая ошибка/400

	useEffect(() => {
		(async () => {
			try {
				const data = await getGptModel();
				const current = data?.model || GPT_MODELS[0].value;
				setGptModelValue(current);
				setSavedGptModel(current);
				// Модель могли вписать руками — тогда её нет в списке, и поле
				// ручного ввода должно открыться сразу, а не прятать значение.
				setIsGptCustom(!GPT_MODELS.some((m) => m.value === current));
			} catch (error) {
				console.error("Load GPT model error:", error);
				setGptStatus({ ok: false, text: "Не удалось загрузить настройку" });
			} finally {
				setIsGptLoading(false);
			}
		})();
	}, []);

	useEffect(() => {
		(async () => {
			try {
				const data = await getTranslateGptModel();
				const current = data?.model || SERVICE_DEFAULT_GPT_MODEL;
				setTranslateGptModelValue(current);
				setSavedTranslateGptModel(current);
				// Модель могли вписать руками — тогда её нет в списке, и поле
				// ручного ввода должно открыться сразу, а не прятать значение.
				setIsTranslateGptCustom(
					current !== SERVICE_DEFAULT_GPT_MODEL &&
						!GPT_MODELS.some((m) => m.value === current),
				);
			} catch (error) {
				console.error("Load translate GPT model error:", error);
				setTranslateGptStatus({
					ok: false,
					text: "Не удалось загрузить настройку",
				});
			} finally {
				setIsTranslateGptLoading(false);
			}
		})();
	}, []);

	useEffect(() => {
		(async () => {
			try {
				const data = await getTranslateProvider();
				const current = data?.provider || "google";
				setProvider(current);
				setSavedProvider(current);
			} catch (error) {
				console.error("Load translate provider error:", error);
				setStatus({ ok: false, text: "Не удалось загрузить настройку" });
			} finally {
				setIsLoading(false);
			}
		})();
	}, []);

	const handleSave = async () => {
		setIsSaving(true);
		setStatus(null);
		try {
			const data = await setTranslateProvider(provider);
			const current = data?.provider || provider;
			setSavedProvider(current);
			setProvider(current);
			setStatus({ ok: true, text: "Сохранено" });
		} catch (error) {
			console.error("Save translate provider error:", error);
			setStatus({ ok: false, text: "Ошибка сохранения" });
		} finally {
			setIsSaving(false);
		}
	};

	const handleSaveGptModel = async () => {
		setIsGptSaving(true);
		setGptStatus(null);
		try {
			const data = await saveGptModel(gptModel.trim());
			const current = data?.model || gptModel.trim();
			setSavedGptModel(current);
			setGptModelValue(current);
			setGptStatus({ ok: true, text: "Сохранено" });
		} catch (error) {
			console.error("Save GPT model error:", error);
			setGptStatus({ ok: false, text: "Ошибка сохранения" });
		} finally {
			setIsGptSaving(false);
		}
	};

	const handleSaveTranslateGptModel = async () => {
		setIsTranslateGptSaving(true);
		setTranslateGptStatus(null);
		try {
			const value = translateGptModel.trim();
			const data = await saveTranslateGptModel(value);
			const current = data?.model || SERVICE_DEFAULT_GPT_MODEL;
			setSavedTranslateGptModel(current);
			setTranslateGptModelValue(current);
			setTranslateGptStatus({ ok: true, text: "Сохранено" });
		} catch (error) {
			console.error("Save translate GPT model error:", error);
			setTranslateGptStatus({ ok: false, text: "Ошибка сохранения" });
		} finally {
			setIsTranslateGptSaving(false);
		}
	};

	const handleTestTranslate = async () => {
		setIsTranslating(true);
		setTranslateResult(null);
		setTranslateError(null);
		try {
			const data = await testTranslate(translateText.trim(), translateLang);
			setTranslateResult(data);
		} catch (error) {
			console.error("Test translate error:", error);
			setTranslateError(error?.message || "Запрос не удался");
		} finally {
			setIsTranslating(false);
		}
	};

	const handleTestGpt = async () => {
		setIsTesting(true);
		setTestResult(null);
		setTestError(null);
		try {
			const data = await testGpt(testPrompt.trim());
			setTestResult(data);
		} catch (error) {
			console.error("Test GPT error:", error);
			setTestError(error?.message || "Запрос не удался");
		} finally {
			setIsTesting(false);
		}
	};

	const gptSelectValue = isGptCustom ? CUSTOM_GPT_MODEL : gptModel;

	const translateGptSelectValue = isTranslateGptCustom
		? CUSTOM_GPT_MODEL
		: translateGptModel;

	// Сервер берёт модель из базы, поэтому проверять несохранённый выбор
	// бессмысленно: ответил бы старый вариант, и это выглядело бы как «новая
	// модель работает». Пока изменение не сохранено, кнопку держим закрытой.
	const hasUnsavedGptModel = gptModel.trim() !== savedGptModel;

	// Тот же расчёт для движка перевода: сервер берёт его из базы, поэтому
	// проверять несохранённый выбор нечего.
	const hasUnsavedProvider = provider !== savedProvider;

	const providerLabel = (value) =>
		PROVIDERS.find((p) => p.value === value)?.label || value;

	const langLabel = (value) =>
		TRANSLATE_LANGS.find((l) => l.value === value)?.label || value;

	return (
		<div className="settings-page">
			<h2>Настройки</h2>

			<section className="settings-page__card">
				<h3>Перевод текстов</h3>
				<p className="settings-page__hint">
					Какой движок использует сервис переводов (translate.jerm.am) для
					кнопки «Перевести» в приложении. Применяется сразу для всех
					пользователей.
				</p>

				<div className="settings-page__row">
					<select
						value={provider}
						disabled={isLoading || isSaving}
						onChange={(e) => {
							setProvider(e.target.value);
							setStatus(null);
						}}
					>
						{PROVIDERS.map((p) => (
							<option key={p.value} value={p.value}>
								{p.label}
							</option>
						))}
					</select>

					<button
						type="button"
						disabled={isLoading || isSaving || provider === savedProvider}
						onClick={handleSave}
					>
						{isSaving ? "Сохранение..." : "Сохранить"}
					</button>
				</div>

				{status && (
					<p
						className={`settings-page__status ${
							status.ok ? "is-ok" : "is-error"
						}`}
					>
						{status.text}
					</p>
				)}
			</section>

			{/* Настройка имеет смысл только когда переводит ChatGPT, поэтому при
			    других движках карточку не показываем вовсе. Ориентируемся на
			    выбранное в списке значение, а не на сохранённое: переключил на
			    ChatGPT — поле появилось сразу, ещё до сохранения. Само значение
			    при этом никуда не девается, вернёте ChatGPT — увидите прежний
			    выбор. */}
			{provider === "chatgpt" && (
			<section className="settings-page__card">
				<h3>Модель GPT для перевода</h3>
				<p className="settings-page__hint">
					Действует, только когда перевод делает <b>ChatGPT</b>: либо он выбран
					движком выше, либо сработал как запасной, когда выбранный движок
					отказал. Google и Yandex модель игнорируют. Это <b>отдельная</b>{" "}
					настройка от «Модели GPT» ниже — та отвечает за разбор совместимости.
					Если нужной модели нет в списке, выберите «Другая» и впишите её имя.
				</p>

				<div className="settings-page__row">
					<select
						value={translateGptSelectValue}
						disabled={isTranslateGptLoading || isTranslateGptSaving}
						onChange={(e) => {
							const value = e.target.value;
							setTranslateGptStatus(null);
							if (value === CUSTOM_GPT_MODEL) {
								setIsTranslateGptCustom(true);
								return;
							}
							setIsTranslateGptCustom(false);
							setTranslateGptModelValue(value);
						}}
					>
						{/* Пустой вариант нужен как способ отменить выбор: без него
						    однажды заданную модель нельзя было бы вернуть к той, что
						    прописана в самом сервисе переводов. */}
						<option value={SERVICE_DEFAULT_GPT_MODEL}>
							Как в сервисе переводов (не задавать)
						</option>
						{GPT_MODELS.map((m) => (
							<option key={m.value} value={m.value}>
								{m.label}
							</option>
						))}
						<option value={CUSTOM_GPT_MODEL}>Другая</option>
					</select>

					{isTranslateGptCustom && (
						<input
							type="text"
							value={translateGptModel}
							placeholder="например, gpt-4.1-mini"
							disabled={isTranslateGptLoading || isTranslateGptSaving}
							onChange={(e) => {
								setTranslateGptModelValue(e.target.value);
								setTranslateGptStatus(null);
							}}
						/>
					)}

					<button
						type="button"
						disabled={
							isTranslateGptLoading ||
							isTranslateGptSaving ||
							translateGptModel.trim() === savedTranslateGptModel
						}
						onClick={handleSaveTranslateGptModel}
					>
						{isTranslateGptSaving ? "Сохранение..." : "Сохранить"}
					</button>
				</div>

				{/* Модель едет тем же запросом, что и движок, и лежит в том же кеше
				    на 60 секунд — предупреждение про задержку то же самое. */}
				<p className="settings-page__hint settings-page__hint--beside">
					Смена модели может применяться до минуты: сервер держит настройку в
					кеше. Переводы, сделанные прежней моделью, лежат в кеше сервиса
					переводов отдельно, поэтому после смены тот же текст будет переведён
					заново.
				</p>

				{translateGptStatus && (
					<p
						className={`settings-page__status ${
							translateGptStatus.ok ? "is-ok" : "is-error"
						}`}
					>
						{translateGptStatus.text}
					</p>
				)}
			</section>
			)}

			<section className="settings-page__card">
				<h3>Проверка перевода</h3>
				<p className="settings-page__hint">
					Напишите текст на любом языке, выберите, на какой перевести, и
					нажмите «Перевести». Исходный язык определяет сам сервис переводов —
					указывать его не нужно. Проверяется <b>сохранённый</b> движок{" "}
					<b>{isLoading ? "..." : providerLabel(savedProvider)}</b>: сервер
					берёт его из базы, а не из списка выше.
				</p>

				<div className="settings-page__row">
					<textarea
						className="settings-page__textarea"
						value={translateText}
						rows={3}
						maxLength={TRANSLATE_TEST_MAX_LENGTH}
						placeholder="Например: привет, как дела?"
						disabled={isTranslating}
						onChange={(e) => setTranslateText(e.target.value)}
					/>
				</div>

				<div className="settings-page__row">
					<select
						value={translateLang}
						disabled={isTranslating}
						onChange={(e) => setTranslateLang(e.target.value)}
					>
						{TRANSLATE_LANGS.map((l) => (
							<option key={l.value} value={l.value}>
								{l.label}
							</option>
						))}
					</select>

					<button
						type="button"
						disabled={
							isTranslating ||
							isLoading ||
							hasUnsavedProvider ||
							translateText.trim().length === 0
						}
						onClick={handleTestTranslate}
					>
						{isTranslating ? "Перевод идёт..." : "Перевести"}
					</button>

					<p className="settings-page__hint settings-page__hint--beside">
						{isTranslating
							? "Ответ может идти до минуты, не закрывайте страницу."
							: hasUnsavedProvider
								? "Сначала сохраните выбранный движок."
								: `Не длиннее ${TRANSLATE_TEST_MAX_LENGTH} символов.`}
					</p>
				</div>

				{/* Движок читается с кешем на 60 секунд, поэтому сразу после
				    сохранения проверка может уйти на прежнем — это не значит, что
				    настройка не применилась. */}
				<p className="settings-page__hint settings-page__hint--beside">
					Смена движка может применяться до минуты: сервер держит выбор в
					кеше. Если ниже показан прежний движок — повторите проверку через
					минуту.
				</p>

				{translateError && (
					<p className="settings-page__status is-error">{translateError}</p>
				)}

				{translateResult && (
					<div className="settings-page__result">
						{translateResult.error ? (
							<p className="settings-page__status is-error">
								Сервис переводов ({providerLabel(translateResult.provider)},{" "}
								{translateResult.ms} мс): {translateResult.error}
							</p>
						) : (
							<>
								<p className="settings-page__status is-ok">
									Перевод на <b>{langLabel(translateResult.targetLang)}</b>{" "}
									движком <b>{providerLabel(translateResult.provider)}</b>,{" "}
									{translateResult.ms} мс.
								</p>
								{/* Сервис при отказе выбранного движка молча уходит на
								    Google, затем на ChatGPT. Без этой строки проверка
								    выглядела бы как «выбранный движок работает». */}
								{translateResult.requestedProvider &&
									translateResult.requestedProvider !==
										translateResult.provider && (
										<p className="settings-page__status is-error">
											Внимание: запрашивали{" "}
											<b>{providerLabel(translateResult.requestedProvider)}</b>,
											но ответил не он — перевод сделал запасной движок.
										</p>
									)}
								<pre className="settings-page__answer">
									{translateResult.text}
								</pre>
							</>
						)}
					</div>
				)}
			</section>

			<section className="settings-page__card">
				<h3>Модель GPT</h3>
				<p className="settings-page__hint">
					Какую модель использует разбор совместимости. Применяется сразу для
					всех пользователей. Если нужной модели нет в списке, выберите
					«Другая» и впишите её имя.
				</p>

				<div className="settings-page__row">
					<select
						value={gptSelectValue}
						disabled={isGptLoading || isGptSaving}
						onChange={(e) => {
							const value = e.target.value;
							setGptStatus(null);
							if (value === CUSTOM_GPT_MODEL) {
								setIsGptCustom(true);
								return;
							}
							setIsGptCustom(false);
							setGptModelValue(value);
						}}
					>
						{GPT_MODELS.map((m) => (
							<option key={m.value} value={m.value}>
								{m.label}
							</option>
						))}
						<option value={CUSTOM_GPT_MODEL}>Другая</option>
					</select>

					{isGptCustom && (
						<input
							type="text"
							value={gptModel}
							placeholder="например, gpt-4.1-mini"
							disabled={isGptLoading || isGptSaving}
							onChange={(e) => {
								setGptModelValue(e.target.value);
								setGptStatus(null);
							}}
						/>
					)}

					<button
						type="button"
						disabled={
							isGptLoading ||
							isGptSaving ||
							gptModel.trim().length === 0 ||
							gptModel.trim() === savedGptModel
						}
						onClick={handleSaveGptModel}
					>
						{isGptSaving ? "Сохранение..." : "Сохранить"}
					</button>
				</div>

				{gptStatus && (
					<p
						className={`settings-page__status ${
							gptStatus.ok ? "is-ok" : "is-error"
						}`}
					>
						{gptStatus.text}
					</p>
				)}
			</section>

			<section className="settings-page__card">
				<h3>Проверка модели</h3>
				<p className="settings-page__hint">
					Задайте любой вопрос и посмотрите, отвечает ли модель. Проверяется
					<b> сохранённая</b> модель{" "}
					<b>{isGptLoading ? "..." : savedGptModel}</b> — сервер берёт её из
					базы, а не из выпадающего списка выше. Запрос идёт тем же путём, что
					и разбор совместимости: сначала прокси gpt.jerm.am, при отказе —
					OpenAI напрямую.
				</p>

				<div className="settings-page__row">
					<textarea
						className="settings-page__textarea"
						value={testPrompt}
						rows={3}
						maxLength={GPT_TEST_MAX_LENGTH}
						placeholder="Например: назови себя и скажи, какая ты модель"
						disabled={isTesting}
						onChange={(e) => setTestPrompt(e.target.value)}
					/>
				</div>

				<div className="settings-page__row">
					<button
						type="button"
						disabled={
							isTesting ||
							isGptLoading ||
							hasUnsavedGptModel ||
							testPrompt.trim().length === 0
						}
						onClick={handleTestGpt}
					>
						{isTesting ? "Запрос идёт..." : "Спросить"}
					</button>
					<p className="settings-page__hint settings-page__hint--beside">
						{isTesting
							? "Ответ может идти до минуты, не закрывайте страницу."
							: hasUnsavedGptModel
								? "Сначала сохраните выбранную модель."
								: `Не длиннее ${GPT_TEST_MAX_LENGTH} символов.`}
					</p>
				</div>

				{testError && (
					<p className="settings-page__status is-error">{testError}</p>
				)}

				{testResult && (
					<div className="settings-page__result">
						{testResult.source === "none" ? (
							<p className="settings-page__status is-error">
								Ответа нет: не сработал ни прокси, ни OpenAI.
							</p>
						) : (
							<>
								<p className="settings-page__status is-ok">
									Ответила модель <b>{testResult.model}</b> —{" "}
									{GPT_SOURCE_LABELS[testResult.source] || testResult.source},{" "}
									{testResult.source === "jerm"
										? testResult.jermMs
										: testResult.openAiMs}{" "}
									мс.
								</p>
								<pre className="settings-page__answer">
									{testResult.content}
								</pre>
							</>
						)}

						{/* Отказ прокси показываем всегда, даже когда ответил OpenAI:
						    иначе незаметно, что запросы пошли мимо прокси. */}
						{testResult.jermError && (
							<p className="settings-page__status is-error">
								Прокси gpt.jerm.am ({testResult.jermMs} мс):{" "}
								{testResult.jermError}
							</p>
						)}
						{testResult.openAiError && (
							<p className="settings-page__status is-error">
								OpenAI ({testResult.openAiMs} мс): {testResult.openAiError}
							</p>
						)}
					</div>
				)}
			</section>

			<MediaSettingsCard />

			<MediaContentTypeCard />

			<WebPushKeysCard />
		</div>
	);
};

export default Settings;
